/**
 * LocationControl — Leaflet custom control for the "Locate me" button
 */

import L from 'leaflet';
import type { LocationManager } from './locationManager.js';
import type { LocationError, LocationPreferences } from './types.js';
import { saveLocationPreferences } from './locationPreferences.js';

type TrackingState = 'idle' | 'requesting' | 'tracking' | 'error';

const BUTTON_LABELS: Record<TrackingState, string> = {
  idle: '📍 Locate me',
  requesting: '⏳ Locating…',
  tracking: '🔴 Stop locating',
  error: '📍 Locate me',
};

export class LocationControl extends L.Control {
  private readonly locationManager: LocationManager;
  private readonly prefs: LocationPreferences;
  private state: TrackingState = 'idle';
  private button: HTMLButtonElement | null = null;
  private errorEl: HTMLParagraphElement | null = null;

  constructor(
    locationManager: LocationManager,
    prefs: LocationPreferences,
    options?: L.ControlOptions
  ) {
    super(options);
    this.locationManager = locationManager;
    this.prefs = prefs;
  }

  onAdd(_map: L.Map): HTMLElement {
    const container = L.DomUtil.create('div', 'leaflet-control location-control');
    L.DomEvent.disableClickPropagation(container);
    L.DomEvent.disableScrollPropagation(container);

    this.button = L.DomUtil.create('button', '', container) as HTMLButtonElement;
    this.errorEl = L.DomUtil.create('p', 'location-error', container) as HTMLParagraphElement;
    this.errorEl.style.display = 'none';

    this.renderButton();

    L.DomEvent.on(this.button, 'click', this.handleClick, this);

    return container;
  }

  onRemove(_map: L.Map): void {
    if (this.button) {
      L.DomEvent.off(this.button, 'click', this.handleClick, this);
    }
  }

  /** Called by main.ts when auto-restore fires successfully */
  setTracking(): void {
    this.transition('tracking');
  }

  private handleClick(): void {
    if (this.state === 'tracking') {
      this.locationManager.stop();
      this.prefs.locationEnabled = false;
      saveLocationPreferences(this.prefs);
      this.transition('idle');
    } else if (this.state === 'idle' || this.state === 'error') {
      this.clearError();
      this.transition('requesting');
      this.locationManager.start(
        false,
        (err) => this.onError(err),
        () => this.onFirstFix()
      );
    }
  }

  private onFirstFix(): void {
    this.prefs.locationEnabled = true;
    saveLocationPreferences(this.prefs);
    this.transition('tracking');
  }

  private onError(err: LocationError): void {
    this.transition('error');
    this.showError(err.userMessage);
    // If we were auto-restoring, correct the stored preference
    if (this.prefs.locationEnabled) {
      this.prefs.locationEnabled = false;
      saveLocationPreferences(this.prefs);
    }
  }

  private transition(newState: TrackingState): void {
    this.state = newState;
    this.renderButton();
  }

  private renderButton(): void {
    if (!this.button) return;
    this.button.textContent = BUTTON_LABELS[this.state];
    this.button.disabled = this.state === 'requesting';
    this.button.className = this.state === 'tracking' ? 'active' : '';
  }

  private showError(message: string): void {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.style.display = 'block';
  }

  private clearError(): void {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.style.display = 'none';
  }
}
